// Tags are case-insensitive on the server, so "fm" and "Fm" are the same tag.
const isSameTag = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

// Mirrors the server's filterTag: trims whitespace at the ends and drops control
// characters, so the client compares and sends the name the server will store.
export const normalizeTag = (tag: string) =>
    // eslint-disable-next-line no-control-regex
    tag.replace(/^[ \t\n\r]+|[ \t\n\r]+$/g, "").replace(/[\x00-\x1f\x7f]/g, "");

// Prefers an exact match, in case case-variants of the same tag already exist.
const findTag = (tags: string[], value: string) =>
    tags.find((tag) => tag === value) ?? tags.find((tag) => isSameTag(tag, value));

export const withoutTags = (tags: string[], exclude: string[]) =>
    tags.filter((tag) => findTag(exclude, tag) === undefined);

// The values to show in the tag menu. The raw input is only offered when no
// existing or already selected tag matches it, otherwise it looks like a separate
// tag that can be created.
export const getTagOptions = (tags: string[], selectedTags: string[], rawInput: string) => {
    const currentInput = normalizeTag(rawInput);
    return currentInput &&
        findTag(tags, currentInput) === undefined &&
        findTag(selectedTags, currentInput) === undefined
        ? [currentInput, ...tags]
        : tags;
};

type TTagSelection =
    | { kind: "empty" }
    | { kind: "alreadySelected" }
    | { kind: "existing"; tag: string }
    | { kind: "new"; tag: string };

// Enter/comma selects the raw text, so map it onto an existing tag if one
// matches case-insensitively instead of trying to create a duplicate.
export const resolveTagSelection = (
    rawInput: string,
    availableTags: string[],
    selectedTags: string[]
): TTagSelection => {
    const input = normalizeTag(rawInput);
    if (input === "") {
        return { kind: "empty" };
    }
    if (findTag(selectedTags, input) !== undefined) {
        return { kind: "alreadySelected" };
    }
    const existingTag = findTag(availableTags, input);
    return existingTag === undefined
        ? { kind: "new", tag: input }
        : { kind: "existing", tag: existingTag };
};
